import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Button as ZusButton, Chip as ZusChip } from '@zep-us/zus';
import { ChevronRightIcon } from '@zep-us/zus-icons';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import '@/shared/features/phone-verification/tracking/events.quiz-trial';
import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';
import { useOpenWindow } from '@/hooks';
import { CouponFillIcon, ExportIcon, XIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { useCurrentPlanChangeGuard, usePaymentFlow } from '@/module/payment';
import { getPaymentLoginUrl } from '@/module/payment/shared/utils/paymentEntryUrl';
import { getInquiryLinkByCountryCode } from '@/module/plan/constants/inquiryLinks';
import { useCountryCode } from '@/shared/hooks';
import { Button, DialogTitle, Drawer, DrawerContent, IconButton, ResponsiveDialog } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { BasicPricingCard, ProPricingCard } from '../PricingCard';
import { Features } from './Features';
import { RestrictedPlanDialog } from './RestrictedPlanDialog';
import { SelectableCard } from './SelectableCard';
import { Titles } from './Titles';
import { resolveUpsellingPlanState } from './upsellingPlanState';
import { getUpsellingCardCta, type PaidPlanType, resolveInitialPlanType, type UpsellingCtaAction } from './upsellingPolicy';
type UpsellingModalCtaTarget = 'payment-flow' | 'pricing';
const UPSELLING_MOBILE_MAX_WIDTH = 810;
type UpsellingIntentProps = {
  intent?: 'NEW_PURCHASE';
  startDate?: never;
} | {
  intent: 'FUTURE_PURCHASE';
  startDate: string;
};
const getUpsellingIntentProps = (intent: UpsellingIntentProps['intent'], startDate?: string): UpsellingIntentProps => intent === 'FUTURE_PURCHASE' ? {
  intent,
  startDate: startDate!
} : {
  intent: intent ?? 'NEW_PURCHASE'
};
type UpsellingModalOptions = {
  initialPlanType?: PaidPlanType;
  from: string;
  ctaTarget?: UpsellingModalCtaTarget;
  onClose?: VoidFunction;
  /**
   * 휴대폰 인증 무료 체험 배너 노출 여부.
   * 적격 판정은 usePremiumAccess 가 열기 전에 끝낸다 — 배너가 직접 조회하지 않는다.
   */
  showPhoneTrialBanner?: boolean;
} & UpsellingIntentProps;
export const upsellingModal = {
  open({
    initialPlanType,
    intent = 'NEW_PURCHASE',
    from,
    ctaTarget = 'payment-flow',
    startDate,
    onClose,
    showPhoneTrialBanner = false
  }: UpsellingModalOptions) {
    overlay.open(({
      unmount
    }) => <UpsellingModal {...getUpsellingIntentProps(intent, startDate)} initialSelectedPlan={initialPlanType} from={from} ctaTarget={ctaTarget} showPhoneTrialBanner={showPhoneTrialBanner} onClose={() => {
      unmount();
      onClose?.();
    }} />);
  }
};
type UpsellingModalProps = {
  initialSelectedPlan?: PaidPlanType;
  from: string;
  ctaTarget: UpsellingModalCtaTarget;
  onClose?: VoidFunction;
  /** @see UpsellingModalOptions.showPhoneTrialBanner */
  showPhoneTrialBanner?: boolean;
} & UpsellingIntentProps;
export const UpsellingModal = ({
  initialSelectedPlan,
  intent = 'NEW_PURCHASE',
  from,
  ctaTarget,
  startDate,
  onClose,
  showPhoneTrialBanner = false
}: UpsellingModalProps) => {
  const {
    user
  } = useUser();
  const [resolvedInitialPlanType] = useState(() => resolveInitialPlanType({
    currentPlan: user?.planType,
    initialPlanType: initialSelectedPlan
  }));
  const hasLogged = useRef(false);
  const isMobile = useMediaQuery({
    query: `(max-width: ${UPSELLING_MOBILE_MAX_WIDTH}px)`
  });
  useEffect(() => {
    if (hasLogged.current) return;
    hasLogged.current = true;

    // 레거시 로깅
    logger.track('proplan_show', {
      location: from,
      // eslint-disable-next-line no-restricted-syntax -- 의도적 예외: 이 이벤트의 planType 은 '유저의 플랜'이 아니라 '클릭·노출된 요금제'다. global 을 덮지만 리네임하면 기존 대시보드 데이터와 단절되므로 의도적으로 유지한다. 이 이벤트로 유저 플랜 세그먼트를 세지 말 것 — global 값은 isPro 로 확인한다.
      planType: resolvedInitialPlanType
    });
    logger.track('open_upselling_modal', {
      from,
      // eslint-disable-next-line no-restricted-syntax -- 의도적 예외: 이 이벤트의 planType 은 '유저의 플랜'이 아니라 '클릭·노출된 요금제'다. global 을 덮지만 리네임하면 기존 대시보드 데이터와 단절되므로 의도적으로 유지한다. 이 이벤트로 유저 플랜 세그먼트를 세지 말 것 — global 값은 isPro 로 확인한다.
      planType: resolvedInitialPlanType
    });
  }, [from, resolvedInitialPlanType]);
  const handleClose = () => {
    if (showPhoneTrialBanner) {
      trackEvent('quiz_trial_prompt_close', {
        trial_source: from,
        surface: 'upselling_banner',
        reason: 'dismiss',
        trial_region: 'kr',
        trial_type: 'phone_verification'
      });
    }
    onClose?.();
  };
  return <>
      {isMobile ? <MobileView initialSelectedPlan={resolvedInitialPlanType} {...getUpsellingIntentProps(intent, startDate)} from={from} ctaTarget={ctaTarget} onClose={handleClose} showPhoneTrialBanner={showPhoneTrialBanner} /> : <DesktopView emphasizedPlan={resolvedInitialPlanType} {...getUpsellingIntentProps(intent, startDate)} from={from} ctaTarget={ctaTarget} onClose={handleClose} showPhoneTrialBanner={showPhoneTrialBanner} />}
    </>;
};

/**
 * 액션 → 버튼 문구 매핑.
 *
 * `Record` 로 고정해 두면 {@link UpsellingCtaAction} 에 액션을 추가했을 때 여기가
 * 컴파일 에러로 걸린다. 예전처럼 조건문으로 늘어놓으면 빠뜨린 액션이 조용히
 * "구매하기"로 떨어져 잘못된 동선을 안내하게 된다.
 *
 * 문구는 반드시 `common` 네임스페이스에서 가져온다. 이 모달은 헤더·사이드바·
 * paywall 등 어디서든 열리는데, `common` 외 네임스페이스는 페이지가
 * `serverSideTranslations` 에 직접 명시해야 로드된다. 명시 안 한 페이지에서는
 * 에러 없이 키 문자열이 그대로 화면에 노출되므로 빌드·테스트로는 못 잡는다.
 */
const useUpsellingCtaLabels = (): Record<UpsellingCtaAction, string> => {
  const {
    t
  } = useTranslation(['common']);
  return {
    purchase: t('common:pricing.pro.purchase'),
    // 선택 모달을 한 번 더 거칠 뿐, 사용자에게는 같은 "구매하기"다.
    'select-plan': t('common:pricing.pro.purchase'),
    extend: t('common:pricing.extendPeriod'),
    upgrade: t('common:pricing.pro.upgradeToPro'),
    'higher-plan-active': t('common:pricing.proPlanInUse'),
    'current-plan': t('common:upselling.currentlyUsing'),
    inquiry: t('common:pricing.pro.inquireNow')
  };
};
const useUpsellingCta = ({
  ctaTarget,
  intent,
  startDate
}: {
  ctaTarget: UpsellingModalCtaTarget;
} & UpsellingIntentProps) => {
  const intentProps = getUpsellingIntentProps(intent, startDate);
  const {
    user,
    isAuthenticated
  } = useUser();
  const {
    countryCode
  } = useCountryCode();
  const {
    startPaymentFlow
  } = usePaymentFlow();
  const openWindow = useOpenWindow();
  const labels = useUpsellingCtaLabels();
  const paymentCountryCode = user?.countryCode ?? countryCode;
  const planState = resolveUpsellingPlanState(user);
  const resolvedIntent = intentProps.intent ?? 'NEW_PURCHASE';

  /**
   * 연장 CTA 가 나올 수 있는 상태에서만 켠다. 프리페치는 렌더를 막지 않으므로
   * 모달 오픈에 로딩이 끼지 않는다 — `resolveUpsellingPlanState` 가 플랜 관리
   * API 를 CTA 판정 입력에서 제외한 이유를 그대로 지킨다.
   */
  const isPlanChangeBlocked = useCurrentPlanChangeGuard({
    enabled: planState.kind === 'individual'
  });
  const getCta = (targetPlan: PaidPlanType) => getUpsellingCardCta({
    state: planState,
    targetPlan,
    intent: resolvedIntent
  });
  const startPurchase = (targetPlan: PaidPlanType) => {
    const entry = resolvedIntent === 'FUTURE_PURCHASE' ? {
      kind: 'purchase' as const,
      plan: targetPlan,
      startDate: intentProps.startDate
    } : {
      kind: 'purchase' as const,
      plan: targetPlan
    };
    if (!isAuthenticated) {
      return openWindow(getPaymentLoginUrl(entry, paymentCountryCode));
    }
    startPaymentFlow({
      ...entry,
      countryCode: paymentCountryCode
    }, {
      target: 'new-tab'
    });
  };

  /**
   * 활성 플랜을 대상으로 하는 변경 결제. 어떤 플랜인지는 서버가 판정하므로
   * `target: 'active'` 만 넘긴다.
   */
  const startPlanChange = (kind: 'extend' | 'upgrade') => {
    // 새 탭을 열기 전에 동기로 확인한다(비동기면 팝업 차단기에 막힌다).
    if (isPlanChangeBlocked(kind)) return;
    startPaymentFlow({
      kind,
      target: {
        kind: 'active'
      },
      countryCode: paymentCountryCode
    }, {
      target: 'new-tab'
    });
  };
  const execute = (targetPlan: PaidPlanType) => {
    const {
      action
    } = getCta(targetPlan);
    if (ctaTarget === 'pricing') return openWindow('/pricing');
    switch (action) {
      case 'current-plan':
      case 'higher-plan-active':
        return;
      case 'inquiry':
        window.open(getInquiryLinkByCountryCode(paymentCountryCode), '_blank', 'noopener,noreferrer');
        return;

      // 연장·업그레이드는 미인증 상태에서 도달할 수 없다(이용 중인 플랜이 전제).
      case 'extend':
      case 'upgrade':
        return startPlanChange(action);
      case 'select-plan':
        overlay.open(({
          unmount
        }) => <RestrictedPlanDialog onClose={unmount} />);
        return;
      case 'purchase':
        return startPurchase(targetPlan);
    }
  };
  return {
    getCta,
    labels,
    execute,
    startPurchase
  };
};

// KR 전용(향상된 QUIZ AI+ 노출과 동일한 countryCode === 'KR' 기준).
// 상단에 학교·단체 견적 문의(/pricing/quote) 진입 알약.
const SchoolGroupInquiryPill = ({
  className
}: {
  className?: string;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    isKR
  } = useCountryCode();
  const openWindow = useOpenWindow();
  if (!isKR) {
    return null;
  }
  return <div className={cn('flex', className)} data-sentry-component="SchoolGroupInquiryPill" data-sentry-source-file="UpsellingModal.tsx">
      <ZusButton variant="contained" intent="alternative" size="lg" rounded="full" onClick={() => {
      logger.track('click_get_quote_button');
      // 플랜 안내 페이지의 학교·단체 탭 활성 상태로 이동
      openWindow('/pricing?tab=group');
    }} rightSlot={<span className="flex items-center gap-3xs">
            <ZusChip size="md" rounded="default" className="min-w-[26px] border-pink-400/10 bg-pink-50 text-body-sm-semibold text-pink-400">
              {t('common:pricing.schoolGroup.discount40')}
            </ZusChip>
            <ChevronRightIcon size="sm" />
          </span>} data-sentry-element="ZusButton" data-sentry-source-file="UpsellingModal.tsx">
        {t('common:pricing.schoolGroup.question')}
      </ZusButton>
    </div>;
};
const DesktopView = ({
  emphasizedPlan,
  intent,
  from,
  ctaTarget,
  startDate,
  onClose,
  showPhoneTrialBanner
}: {
  emphasizedPlan: PaidPlanType;
  from: string;
  ctaTarget: UpsellingModalCtaTarget;
  onClose?: VoidFunction;
  showPhoneTrialBanner: boolean;
} & UpsellingIntentProps) => {
  const {
    t
  } = useTranslation(['common']);
  const openWindow = useOpenWindow();
  const {
    getCta,
    labels,
    execute,
    startPurchase
  } = useUpsellingCta({
    ctaTarget,
    ...getUpsellingIntentProps(intent, startDate)
  });
  const basicCta = getCta('BASIC');
  const proCta = getCta('PRO');
  const handleClickCta = (planType: PaidPlanType) => {
    // eslint-disable-next-line no-restricted-syntax -- 의도적 예외: 이 이벤트의 planType 은 '유저의 플랜'이 아니라 '클릭·노출된 요금제'다. global 을 덮지만 리네임하면 기존 대시보드 데이터와 단절되므로 의도적으로 유지한다. 이 이벤트로 유저 플랜 세그먼트를 세지 말 것 — global 값은 isPro 로 확인한다.
    logger.track('click_pricing_card_cta', {
      planType,
      from
    });
    execute(planType);
  };

  /** 보조 "구매하기"는 정책 분기를 타지 않고 항상 신규 구매로 간다. */
  const handleClickSecondaryPurchase = (planType: PaidPlanType) => {
    // eslint-disable-next-line no-restricted-syntax -- 의도적 예외: 이 이벤트의 planType 은 '유저의 플랜'이 아니라 '클릭·노출된 요금제'다. global 을 덮지만 리네임하면 기존 대시보드 데이터와 단절되므로 의도적으로 유지한다. 이 이벤트로 유저 플랜 세그먼트를 세지 말 것 — global 값은 isPro 로 확인한다.
    logger.track('click_pricing_card_cta', {
      planType,
      from
    });
    startPurchase(planType);
  };
  return <ResponsiveDialog size="2xl" className="flex-col" onClose={onClose} data-sentry-element="ResponsiveDialog" data-sentry-component="DesktopView" data-sentry-source-file="UpsellingModal.tsx">
      <DialogTitle className="sr-only" data-sentry-element="DialogTitle" data-sentry-source-file="UpsellingModal.tsx">
        {t('common:pricing.planGuide')}
      </DialogTitle>
      <div className="flex min-h-0 flex-1 flex-row">
        <SideSection data-sentry-element="SideSection" data-sentry-source-file="UpsellingModal.tsx" />
        <main className="relative flex w-full min-w-0 flex-col px-xl">
          <header className="flex shrink-0 flex-wrap items-center gap-md pb-md pt-lg">
            <SchoolGroupInquiryPill data-sentry-element="SchoolGroupInquiryPill" data-sentry-source-file="UpsellingModal.tsx" />
            <div className="ml-auto flex items-center gap-sm">
              <Button variant="transparent" intent="neutral" size="lg" rightSlot={<ExportIcon size="md" />} onClick={() => openWindow('/pricing')} data-sentry-element="Button" data-sentry-source-file="UpsellingModal.tsx">
                <span className="[text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
                  {t('common:pricing.planGuide')}
                </span>
              </Button>
              <IconButton variant="transparent" intent="neutral" size="lg" aria-label={t('common:global.close.button')} onClick={() => onClose?.()} data-sentry-element="IconButton" data-sentry-source-file="UpsellingModal.tsx">
                <XIcon size="md" data-sentry-element="XIcon" data-sentry-source-file="UpsellingModal.tsx" />
              </IconButton>
            </div>
          </header>
          <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto">
            <section className="flex w-full flex-col justify-center gap-md px-3xs pb-xl tablet-lg:flex-row">
              <BasicPricingCard isEmphasized={emphasizedPlan === 'BASIC'} className="min-w-0 hover:shadow-lg" ctaButtonText={labels[basicCta.action]} ctaButtonDisabled={basicCta.disabled} onCtaButtonClick={() => handleClickCta('BASIC')} secondaryCtaText={basicCta.hasSecondaryPurchase ? t('common:detail.planPurchase') : undefined} onSecondaryCtaClick={() => handleClickSecondaryPurchase('BASIC')} data-sentry-element="BasicPricingCard" data-sentry-source-file="UpsellingModal.tsx" />
              <ProPricingCard isEmphasized={emphasizedPlan === 'PRO'} className="min-w-0 hover:shadow-lg" ctaButtonText={labels[proCta.action]} ctaButtonDisabled={proCta.disabled} onCtaButtonClick={() => handleClickCta('PRO')} secondaryCtaText={proCta.hasSecondaryPurchase ? t('common:detail.planPurchase') : undefined} onSecondaryCtaClick={() => handleClickSecondaryPurchase('PRO')} data-sentry-element="ProPricingCard" data-sentry-source-file="UpsellingModal.tsx" />
            </section>
          </div>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-xl bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,rgba(255,255,255,1)_30%)]" />
        </main>
      </div>
      {/* 시안(28913:52359) — 사이드 이미지까지 걸치는 모달 최하단 전체 폭 바 */}
      <PhoneTrialUpsellBanner show={showPhoneTrialBanner} from={from} data-sentry-element="PhoneTrialUpsellBanner" data-sentry-source-file="UpsellingModal.tsx" />
    </ResponsiveDialog>;
};
const MobileView = ({
  initialSelectedPlan,
  intent,
  from,
  ctaTarget,
  startDate,
  onClose,
  showPhoneTrialBanner
}: {
  initialSelectedPlan: PaidPlanType;
  from: string;
  ctaTarget: UpsellingModalCtaTarget;
  onClose?: VoidFunction;
  showPhoneTrialBanner: boolean;
} & UpsellingIntentProps) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    countryCode
  } = useCountryCode();
  const openWindow = useOpenWindow();
  const [selectedPlan, setSelectedPlan] = useState<PaidPlanType>(initialSelectedPlan);
  const {
    getCta,
    labels,
    execute,
    startPurchase
  } = useUpsellingCta({
    ctaTarget,
    ...getUpsellingIntentProps(intent, startDate)
  });
  const ctaState = getCta(selectedPlan);
  const handleClickCta = () => {
    logger.track('click_upselling_modal_mobile_cta', {
      from,
      // eslint-disable-next-line no-restricted-syntax -- 의도적 예외: 이 이벤트의 planType 은 '유저의 플랜'이 아니라 '클릭·노출된 요금제'다. global 을 덮지만 리네임하면 기존 대시보드 데이터와 단절되므로 의도적으로 유지한다. 이 이벤트로 유저 플랜 세그먼트를 세지 말 것 — global 값은 isPro 로 확인한다.
      planType: selectedPlan
    });
    execute(selectedPlan);
  };
  const handleClickSecondaryPurchase = () => {
    logger.track('click_upselling_modal_mobile_cta', {
      from,
      // eslint-disable-next-line no-restricted-syntax -- 의도적 예외: 이 이벤트의 planType 은 '유저의 플랜'이 아니라 '클릭·노출된 요금제'다. global 을 덮지만 리네임하면 기존 대시보드 데이터와 단절되므로 의도적으로 유지한다. 이 이벤트로 유저 플랜 세그먼트를 세지 말 것 — global 값은 isPro 로 확인한다.
      planType: selectedPlan
    });
    startPurchase(selectedPlan);
  };
  const primaryLabel = labels[ctaState.action];
  const primaryCtaIntent = selectedPlan === 'PRO' ? 'gradient' : 'strong';
  return <Drawer open={true} data-sentry-element="Drawer" data-sentry-component="MobileView" data-sentry-source-file="UpsellingModal.tsx">
      <DrawerContent className="!max-h-[calc(90%-env(safe-area-inset-top))] w-full" hasDrawerHandle hasDim onDimClick={() => onClose?.()} data-sentry-element="DrawerContent" data-sentry-source-file="UpsellingModal.tsx">
        <DialogTitle className="sr-only" data-sentry-element="DialogTitle" data-sentry-source-file="UpsellingModal.tsx">
          {t('common:pricing.planGuide')}
        </DialogTitle>
        {/* 시안(28913:52359) — 모바일은 최상단 전체 폭. 좌우 패딩 밖에 둔다. */}
        <PhoneTrialUpsellBanner show={showPhoneTrialBanner} from={from} className="mt-[6px]" data-sentry-element="PhoneTrialUpsellBanner" data-sentry-source-file="UpsellingModal.tsx" />
        <div className="flex flex-col gap-lg overflow-y-auto bg-[linear-gradient(180deg,#FFF_0%,#E7F4FF_37.11%,#F3F2FF_74.93%)] px-md pb-[168px] pt-md">
          <Titles data-sentry-element="Titles" data-sentry-source-file="UpsellingModal.tsx" />
          <SchoolGroupInquiryPill className="justify-center pt-xs" data-sentry-element="SchoolGroupInquiryPill" data-sentry-source-file="UpsellingModal.tsx" />

          <section className="flex flex-col gap-md px-xs">
            <div className="flex flex-col gap-xs">
              <SelectableCard planType="BASIC" isSelected={selectedPlan === 'BASIC'} onSelect={setSelectedPlan} data-sentry-element="SelectableCard" data-sentry-source-file="UpsellingModal.tsx" />
              <SelectableCard planType="PRO" isSelected={selectedPlan === 'PRO'} onSelect={setSelectedPlan} data-sentry-element="SelectableCard" data-sentry-source-file="UpsellingModal.tsx" />
            </div>
            <Features planType={selectedPlan} countryCode={countryCode} data-sentry-element="Features" data-sentry-source-file="UpsellingModal.tsx" />
          </section>

          <footer className="absolute bottom-0 left-0 z-[2] flex w-full flex-col gap-xs bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,white_10%,white_100%)] p-md">
            {ctaState.hasSecondaryPurchase ?
          // 이용 중인 사용자에게는 연장·업그레이드가 주 동선이지만, 신규 구매도
          // 함께 열어 둔다 (Figma 14773:41182).
          <div className="flex w-full gap-xs">
                <Button size="lg" intent="neutral" className="flex-1 text-neutral disabled:text-neutral" onClick={handleClickSecondaryPurchase}>
                  {t('common:detail.planPurchase')}
                </Button>
                <Button size="lg" intent={primaryCtaIntent} className="flex-1" disabled={ctaState.disabled} onClick={handleClickCta}>
                  {primaryLabel}
                </Button>
              </div> : <Button size="lg" intent={primaryCtaIntent} disabled={ctaState.disabled} onClick={handleClickCta}>
                {primaryLabel}
              </Button>}
            <Button size="md" variant="transparent" intent="neutral" rightSlot={<ExportIcon size="md" />} onClick={() => openWindow('/pricing')} data-sentry-element="Button" data-sentry-source-file="UpsellingModal.tsx">
              <span>{t('common:pricing.planGuide')}</span>
            </Button>
          </footer>
        </div>
      </DrawerContent>
    </Drawer>;
};

/**
 * 적격 판정은 모달을 열기 전에 usePremiumAccess 가 끝낸다.
 * 여기서 다시 조회하면 모달이 뜬 뒤 배너가 끼어들며 레이아웃이 밀리므로, 받은 대로만 그린다.
 */
const PhoneTrialUpsellBanner = ({
  className,
  show,
  from
}: {
  className?: string;
  show: boolean;
  from: string;
}) => show ? <PhoneTrialUpsellBannerContent className={className} from={from} /> : null;

/**
 * 훅은 실제로 그릴 때만 부른다. show 판정을 훅 뒤에 두면 배너가 꺼져 있어도
 * 라우터를 요구하게 되고, 모달을 라우터 없이 렌더하는 곳에서 통째로 터진다.
 */
const PhoneTrialUpsellBannerContent = ({
  className,
  from
}: {
  className?: string;
  from: string;
}) => {
  const router = useRouter();
  const {
    t
  } = useTranslation(['common']);
  useEffect(() => {
    trackEvent('quiz_trial_prompt_view', {
      trial_source: from,
      surface: 'upselling_banner',
      trial_region: 'kr',
      trial_type: 'phone_verification'
    });
  }, [from]);
  return <div className={cn('flex h-12 max-h-12 w-full shrink-0 items-center justify-center gap-sm border-t border-assistive bg-alternative py-0', className)} data-sentry-component="PhoneTrialUpsellBannerContent" data-sentry-source-file="UpsellingModal.tsx">
      <span className="flex items-center gap-2xs">
        <CouponFillIcon size="sm" className="text-violet-400" data-sentry-element="CouponFillIcon" data-sentry-source-file="UpsellingModal.tsx" />
        <span className="bg-gradient-to-r from-violet-400 to-blue-400 bg-clip-text text-body-md-bold text-transparent">
          {t('phoneVerify.banner.eventTitle')}
        </span>
      </span>
      <Button variant="contained" intent="alternative" size="2xs" rounded="full" className="shrink-0 bg-white text-alternative" onClick={() => {
      trackEvent('quiz_trial_prompt_click', {
        trial_source: from,
        surface: 'upselling_banner',
        destination: 'verification_phone',
        trial_region: 'kr',
        trial_type: 'phone_verification'
      });
      void router.push({
        pathname: '/verification-phone',
        query: {
          from: 'banner_modal',
          trialSource: from,
          callbackUrl: router.asPath
        }
      });
    }} data-sentry-element="Button" data-sentry-source-file="UpsellingModal.tsx">
        {t('phoneVerify.banner.eventCta')}
      </Button>
    </div>;
};
const SideSection = () => {
  return <aside className="relative w-[240px] shrink-0 bg-[url('/assets/upselling_background.webp')] bg-cover tablet-lg:w-[300px]" data-sentry-component="SideSection" data-sentry-source-file="UpsellingModal.tsx">
      <div className="flex flex-col items-center gap-lg px-lg py-3xl">
        <Titles data-sentry-element="Titles" data-sentry-source-file="UpsellingModal.tsx" />
      </div>
      <div className="absolute bottom-0 left-0 z-[1] h-[180px] w-full bg-[linear-gradient(180deg,rgba(231,232,255,0.00)_0%,#E7E4FF_100%)]" />
    </aside>;
};